import React, { useCallback, useEffect, useState } from 'react'

import { observer } from 'mobx-react'
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'

import cn from 'classnames'

import {
  auth_token_link,
  clickCoinGeckoAUTH,
  clickWhitepaper,
  FAQ_link,
  main_link,
  marketplace_link,
  versions_link,
  video_feed_link,
} from 'utils'
import { useMediaQuery } from 'utils/hooks'

import { toast } from 'App'
import { CustomTooltip } from 'components/CustomTooltip'
import { DrawerMenu } from 'components/Header/DrawerMenu'
import { InviteModal } from 'components/InviteModal'

import { useStore } from 'store'

import authLogo from 'sources/images/auth.svg'
import authLogoDark from 'sources/images/authDark.svg'
import authTokenIcon from 'sources/images/authToken.svg'
import authIcon from 'sources/images/authToken.svg'
import { ReactComponent as WhitepaperIcon } from 'sources/images/paperSvg.svg'
import { ReactComponent as HowItWorksIcon } from 'sources/images/play.svg'

import { VideoModal } from '../VideoModal'
import { AuthorizedUser } from './AuthorizedUser'
import { BurgerMenu } from './BurgerMenu'
import { NotificationBell } from './NotificationBell'
import Search from './Search'
import { ThemeSwitch } from './ThemeSwitch'
import { UnauthorizedUser } from './UnauthorizedUser'

// import santaHat from 'sources/images/santa_hat.png'
import styles from './styles.module.scss'

interface IProps {
  transparent?: boolean
  darkTheme?: boolean
  lightTheme?: boolean
  linkColor?: string
  positionSticky?: boolean
  withDrawerMenu?: boolean
}

//Resize
const Header: React.FC<IProps> = observer(
  ({
    transparent = false,
    darkTheme,
    lightTheme,
    linkColor,
    positionSticky,
    withDrawerMenu,
  }) => {
    const {
      theme,
      main,
      profile,
      pageActivities,
      loading,
      authToken,
      admin: adminStore,
    } = useStore()
    const location = useLocation()
    const width_1290 = useMediaQuery('(max-width: 1290px)')
    const width_450 = useMediaQuery('(max-width: 450px)')

    const navigate = useNavigate()
    const [isOpenInviteModal, setIsOpenInviteModal] = useState(false)
    const [openVideoModal, setOpenVideoModal] = useState(false)
    const [isVideoReady, setIsVideoReady] = useState(false)

    const handlerOpenInviteModal = () => {
      if (profile.isAuthorizedProfile()) {
        setIsOpenInviteModal(true)
      } else {
        return toast({
          type: 'warning',
          message: `You don't have access to this page. Please connect and select profile.`,
        })
      }
    }

    const handlerClickPrivateRoute = (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
      if (profile.isAuthorizedProfile()) return
      e.preventDefault()
      toast({
        type: 'warning',
        message: `You don't have access to this page. Please connect and select profile.`,
      })
    }

    const redirectToVersionsPage = () => {
      navigate(versions_link())
    }

    const idDrawerMaskVisible = () => {
      return (
        profile.isAuthorizedProfile() &&
        (location.pathname === '/videos' ||
          (location.pathname !== '/video_chat' &&
            location.pathname !== '/video_post' &&
            location.pathname.includes('/video')))
      )
    }

    const onClickWhitepaper = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault()
        clickWhitepaper()
      },
      []
    )

    const onClickHowItWorks = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault()
        if (!isVideoReady) {
          loading.setLoading(true)
        }
        setOpenVideoModal(true)
      },
      [isVideoReady]
    )

    const onClickCoinGecko = (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
      e.preventDefault()
      clickCoinGeckoAUTH()
    }

    useEffect(() => {
      adminStore.getLastVersionNumber()
    }, [])

    if (main.isPWA) {
      if (!positionSticky) return null

      return <div style={{ height: '56px', minHeight: '56px' }} />
    }

    return (
      <div
        className={cn({
          [styles.container]: !transparent,
          [styles.containerTransparent]: transparent,
          [styles.containerPositionSticky]: positionSticky,
        })}
      >
        {withDrawerMenu && profile.isAuthorizedProfile() ? (
          <DrawerMenu />
        ) : null}
        <div
          className={cn({
            [styles.box]: true,
            [styles.colorLinkDarkTheme]: darkTheme,
            [styles.colorLinkLightTheme]: lightTheme,
          })}
        >
          {/*<img*/}
          {/*  onClick={() => navigate(main_link())}*/}
          {/*  src={santaHat}*/}
          {/*  alt="hat"*/}
          {/*  className={styles.santaHat}*/}
          {/*/>*/}
          {idDrawerMaskVisible() ? <div className={styles.drawerMask} /> : null}
          <img
            onClick={() => navigate(main_link())}
            src={
              width_450
                ? authTokenIcon
                : theme.theme === 'light'
                ? authLogo
                : authLogoDark
            }
            alt="logo"
            className={cn({
              [styles.logo]: true,
              [styles.logoSmall]: width_450,
            })}
          />

          <div className={styles.headerMiddleBlock}>
            {/*Version*/}
            <CustomTooltip
              title={'Click to see all versions'}
              placement="right"
              disablePortal
              children={
                <div
                  className={styles.version}
                  onClick={redirectToVersionsPage}
                >
                  v{adminStore.currentAppVersion}
                </div>
              }
            />

            {/*AUTH Price*/}
            <CustomTooltip
              title={
                <div className={styles.AUTHPriceTooltip}>
                  <img src={authIcon} alt="auth" />
                  <div style={{ whiteSpace: 'nowrap' }}>1 AUTH =</div>
                  <div className={styles.rate}>
                    {authToken.AUTHPrice.ETHPriceForAUTH} ETH ($
                    {authToken.AUTHPrice.USDPriceForAUTH.toFixed(4)})
                  </div>
                </div>
              }
              placement="top"
              disablePortal
            >
              <NavLink
                to={''}
                onClick={onClickCoinGecko}
                className={cn({
                  [styles.link]: true,
                  [styles.AUTHPrice]: true,
                })}
              >
                <img src={authTokenIcon} alt="auth" />
                {`1 AUTH = $${authToken.AUTHPrice.USDPriceForAUTH.toFixed(4)}`}
              </NavLink>
            </CustomTooltip>

            {/*Visible additionalLink only when max-width > 720px*/}
            <NavLink
              to={''}
              onClick={onClickHowItWorks}
              className={cn({
                [styles.link]: true,
                [styles.howItWorksLink]: true,
              })}
            >
              <HowItWorksIcon />
              <span>How it Works</span>
            </NavLink>
            <NavLink
              to={''}
              onClick={onClickWhitepaper}
              className={cn({
                [styles.link]: true,
                [styles.whitepaperLink]: true,
              })}
            >
              <WhitepaperIcon className={styles.whitepaperIcon} />
              <span>Whitepaper</span>
            </NavLink>

            {/*Visible when max-width > 1290px*/}
            {!width_1290 ? (
              <>
                <ThemeSwitch />
                <div
                  className={styles.link}
                  style={{ color: linkColor }}
                  onClick={handlerOpenInviteModal}
                >
                  Invite
                </div>
                <NavLink
                  className={styles.link}
                  style={{ color: linkColor }}
                  to={FAQ_link()}
                >
                  Docs
                </NavLink>
                <NavLink
                  onClick={e => handlerClickPrivateRoute(e)}
                  className={styles.link}
                  style={{ color: linkColor }}
                  to={auth_token_link()}
                >
                  <b>AUTH</b> Token
                </NavLink>

                <NavLink
                  onClick={e => handlerClickPrivateRoute(e)}
                  className={styles.link}
                  style={{ color: linkColor }}
                  to={marketplace_link()}
                >
                  Marketplace
                </NavLink>

                <NavLink
                  className={styles.link}
                  style={{ color: linkColor }}
                  to={video_feed_link()}
                >
                  Videos
                </NavLink>
              </>
            ) : null}

            <div className={styles.searchBox}>
              <Search
                linkColor={linkColor}
                transparent={transparent}
                positionSticky={positionSticky}
              />
            </div>
          </div>

          {/*Selected user avatar and Notification Bell  visible only when logged*/}
          {profile.isAuthorizedProfile() ? (
            <div className={styles.authorized}>
              <NotificationBell bellWhite={!positionSticky} />
              <AuthorizedUser />
            </div>
          ) : (
            <>{!width_1290 ? <UnauthorizedUser /> : null}</>
          )}

          {/*Burger Menu---------------------*/}
          {width_1290 ? (
            <BurgerMenu
              onOpenInviteModal={handlerOpenInviteModal}
              onClickHowItWorks={onClickHowItWorks}
              onClickWhitepaper={onClickWhitepaper}
            />
          ) : null}
        </div>
        {isOpenInviteModal ? (
          <InviteModal
            setIsOpen={setIsOpenInviteModal}
            isOpen={isOpenInviteModal}
          />
        ) : null}
        <VideoModal
          withAnimation
          open={openVideoModal}
          setOpen={setOpenVideoModal}
          isVideoReady={isVideoReady}
          setIsVideoReady={setIsVideoReady}
        />
      </div>
    )
  }
)

Header.displayName = 'Header'

export default Header
